import { useState, useEffect } from 'react';
import { Modal, ModalFooter, Button, Input, Select } from '../../../components';
import { StaffMember, CreateStaffData, UpdateStaffData } from '../services/staffService';

const roleOptions = [
  { value: 'agent', label: 'Agent' },
  { value: 'supervisor', label: 'Supervisor' },
];

interface StaffFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateStaffData | UpdateStaffData) => void;
  isLoading: boolean;
  staff?: StaffMember | null;
}

export default function StaffFormModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  staff,
}: StaffFormModalProps) {
  const isEditing = !!staff;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  const [phone, setPhone] = useState('');
  const [department, setDepartment] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (staff) {
      setName(staff.name);
      setEmail(staff.email);
      setRole(staff.role);
      setPhone(staff.phone || '');
      setDepartment(staff.department || '');
    } else {
      setName('');
      setEmail('');
      setRole('agent');
      setPhone('');
      setDepartment('');
    }
    setErrors({});
  }, [staff, isOpen]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = 'Name is required';
    if (!isEditing) {
      if (!email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        newErrors.email = 'Invalid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    if (isEditing) {
      const data: UpdateStaffData = {};
      if (name !== staff!.name) data.name = name;
      if (role !== staff!.role) data.role = role;
      if (phone !== (staff!.phone || '')) data.phone = phone || undefined;
      if (department !== (staff!.department || ''))
        data.department = department || undefined;
      onSubmit(data);
    } else {
      const data: CreateStaffData = {
        email,
        name,
        role,
        ...(phone && { phone }),
        ...(department && { department }),
      };
      onSubmit(data);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Staff Member' : 'Add Staff Member'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="John Doe"
          error={errors.name}
        />

        {!isEditing && (
          <>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              error={errors.email}
            />
            <p className="text-xs text-ink-2">
              Login credentials will be emailed to the new staff member. They will be required to change their password on first login.
            </p>
          </>
        )}

        <Select
          label="Role"
          value={role}
          onChange={setRole}
          options={roleOptions}
        />

        <Input
          label="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 234 567 8900"
        />

        <Input
          label="Department (optional)"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          placeholder="e.g. Customer Support"
        />

        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? 'Save Changes' : 'Add Staff Member'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
